/**
 * Компактный форматтер относительного времени для Inbox («2 ч назад», «вчера»).
 *
 * Just Enough: без `date-fns` (одна функция, не тянет зависимость). Используется
 * в `<InboxPreviewCard>` и `/inbox` карточках. Абсолютное время — в `title`
 * (нативный tooltip) для доступности (INB §4.2).
 *
 * Парсит ISO datetime от Frappe (`PS Message.created_at`, UTC). Локаль — ru.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Возвращает относительное время («только что», «5 мин назад», «2 ч назад»,
 * «вчера», «3 дня назад», fallback на дату для старого).
 */
export function formatRelativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < MINUTE) return 'только что';
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE);
    return `${m} ${pluralMinutes(m)} назад`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR);
    return `${h} ${pluralHours(h)} назад`;
  }
  if (diff < 2 * DAY) return 'вчера';
  if (diff < 7 * DAY) {
    const d = Math.floor(diff / DAY);
    return `${d} ${pluralDays(d)} назад`;
  }
  // Старше недели — абсолютная дата (dd.MM.yyyy).
  return formatAbsolute(iso);
}

/** Абсолютная дата/время для `title` (нативный tooltip, a11y). */
export function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pluralMinutes(n: number): string {
  return plural(n, 'минуту', 'минуты', 'минут');
}
function pluralHours(n: number): string {
  return plural(n, 'час', 'часа', 'часов');
}
function pluralDays(n: number): string {
  return plural(n, 'день', 'дня', 'дней');
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
